import { IconFont, Popover } from '@byecode/ui'
import type { VariableADTvalue } from '@lighthouse/core'
import { isEmpty } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { getInputInnerTypeMatch } from '../../utils'
import { VariableSelectDropDown } from '../Variable'
import type { VariablePickerProps } from './types'
import { VariableTriggerWrapper } from './VariableTriggerWrapper'

const AddIcon = styled.div`
    width: 16px;
    height: 16px;
    padding: 2px;
    font-size: 12px;
    line-height: 0;
    background-color: var(--color-main);
    border-radius: 3px;
`

const Text = styled.span`
    font-size: 12px;
    white-space: nowrap;
    color: var(--color-black);
`

export function VariablePicker({
    triggerWrapper: Target = VariableTriggerWrapper,
    field,
    onSelect,
    disabled,
    zIndex,
    ...restOptions
}: VariablePickerProps) {
    const [open, setOpen] = useState(false)
    const handleSelect = useCallback(
        (value: VariableADTvalue) => {
            setOpen(false)
            onSelect?.(value)
        },
        [onSelect]
    )

    const innerTypes =
        restOptions.innerTypes ??
        (() => {
            if (!field?.innerType) {
                return undefined
            }
            const matched = getInputInnerTypeMatch(field.innerType)
            return Array.isArray(matched) ? matched : matched ? [matched] : undefined
        })()
    const resolvedOptions = innerTypes ? { ...restOptions, innerTypes } : restOptions

    const _disabled = disabled || isEmpty(resolvedOptions)

    const fields = useMemo(() => (field ? [field] : undefined), [field])

    return (
        <>
            <Popover width={248} withinPortal withinOverlay opened={open} disabled={_disabled} zIndex={zIndex} onChange={setOpen}>
                <Popover.Target>
                    {/* Prevent focus from leaving the editor when clicking the trigger */}
                    <div onMouseDown={e => e.preventDefault()}>
                        <Target>
                            <AddIcon>
                                <IconFont type="Add" fill="var(--color-white)" />
                            </AddIcon>
                            <Text>插入变量</Text>
                        </Target>
                    </div>
                </Popover.Target>

                <Popover.Dropdown>
                    {/* Prevent focus from being moved to dropdown items; keep caret in editor */}
                    <div onMouseDown={e => e.preventDefault()}>
                        <VariableSelectDropDown {...resolvedOptions} fields={fields} onChange={handleSelect} />
                    </div>
                </Popover.Dropdown>
            </Popover>
        </>
    )
}
